import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { CoverArt } from "@/components/CoverArt";

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 220]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
            <motion.div style={{ y: yBg }} className="absolute inset-0 opacity-60">
                <CoverArt seed="hero-investidor-comum" variant="line" className="scale-110" />
            </motion.div>
            <div className="absolute inset-0 hero-vignette" />

            <motion.div style={{ opacity }} className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto px-4 md:px-8 w-full pt-32 pb-16">
                <motion.span
                    data-testid="hero-overline"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-xs uppercase tracking-[0.35em] text-gold font-medium mb-6 md:mb-8"
                >
                    Educação financeira sem jargão
                </motion.span>

                <h1 data-testid="hero-headline" className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
                    <MaskedLine delay={0.35}>O seu dinheiro,</MaskedLine>
                    <MaskedLine delay={0.5}>explicado <span className="italic text-gold">com clareza</span> —</MaskedLine>
                    <MaskedLine delay={0.65}>para quem começa do zero.</MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-subtitle"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="mt-6 md:mt-8 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed"
                >
                    Guias diretos sobre orçamento, renda fixa, dividendos e independência financeira.
                    Sem promessa de enriquecimento rápido. Só método, matemática e paciência.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                    className="mt-8 md:mt-10 flex flex-wrap gap-4"
                >
                    <button
                        data-testid="hero-cta-artigos"
                        onClick={() => scrollTo("artigos")}
                        className="group flex items-center gap-2 bg-gold text-ink font-medium px-7 py-3.5 hover:bg-golddark transition-colors"
                    >
                        Explorar artigos
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                        data-testid="hero-cta-manifesto"
                        onClick={() => scrollTo("manifesto")}
                        className="px-7 py-3.5 border border-white/20 text-slate-200 hover:border-gold hover:text-gold transition-colors"
                    >
                        Nosso manifesto
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.5 }}
                className="relative z-10 border-t border-white/10"
            >
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 grid grid-cols-3 gap-4">
                    {[
                        ["9+", "guias publicados"],
                        ["6", "categorias"],
                        ["100%", "gratuito, sempre"],
                    ].map(([num, label], i) => (
                        <div key={i} data-testid={`hero-stat-${i}`} className="flex items-baseline gap-3">
                            <span className="font-serif text-2xl md:text-4xl text-gold">{num}</span>
                            <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span>
                        </div>
                    ))}
                </div>
                <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute right-6 -top-14 md:right-10 text-slate-500"
                >
                    <ArrowDown size={18} />
                </motion.div>
            </motion.div>
        </section>
    );
};
